import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import LogoNav from 'dibs-logo/exports/LogoNav.ts';
import { UserNotifications } from '../Notifications/userNotifications/UserNotifications.tsx';
import { SearchBar } from '../SearchBar/SearchBar.tsx';
import AccountNav from '../AccountNav/AccountNav.tsx';
import Favorites from '../Favorites/Favorites.tsx';
import CartNav from '../CartNav/CartNav.jsx';
import { CartAddedNotification } from '../CartAddedNotification/CartAddedNotification.tsx';
import { useHeaderFooterServerVars } from '../HeaderFooterServerVarsContext/HeaderFooterServerVarsContext.tsx';

import dibsCss from 'dibs-css';
import styles from './TopBar-style.scss';

import { type TopBar_viewer$key } from './__generated__/TopBar_viewer.graphql.ts';
import { type TopBar_user$key } from './__generated__/TopBar_user.graphql.ts';

const DROPDOWN_SHOW_DELAY_MS = 150;
const DROPDOWN_HIDE_DELAY_MS = 200;

const viewerFragment = graphql`
    fragment TopBar_viewer on Viewer {
        ...CartNav_viewer
        ...SearchBar_viewer
        ...AccountNav_viewer
    }
`;

const userFragment = graphql`
    fragment TopBar_user on User {
        ... @include(if: $isClient) {
            ...AccountNav_user
            ...CartNav_user
            ...SearchBar_user
            ...LogoNav_user
            authMetadata {
                emailOnly
            }
        }
    }
`;

type Props = {
    viewer: TopBar_viewer$key;
    user: TopBar_user$key | null | undefined;
    userId: string;
    hasRootPendingRefetch: boolean;
    hasUserId: boolean;
    isClient: boolean;
    isSticky: boolean;
    hasTradeUserId: boolean;
    hasError?: boolean;
    onMouseOver?: () => void;
    onMouseOut?: () => void;
};

const TopBar: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    hasRootPendingRefetch,
    hasUserId,
    isClient,
    userId,
    hasError,
    isSticky,
    hasTradeUserId,
    onMouseOver,
    onMouseOut,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const barClasses = classnames(styles.bar, dibsCss.zBump, {
        [styles.stickyBar]: isSticky,
    });
    const { isHeaderFullWidth } = useHeaderFooterServerVars();
    const isEmailOnly = !!user?.authMetadata?.emailOnly;
    const { isUserTypeTrade, isUserTypeVip } = useHeaderFooterServerVars();

    if (hasError) {
        return (
            <div className={barClasses}>
                <div
                    className={classnames(styles.barContent, dibsCss.flex, dibsCss.justifyBetween)}
                >
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.grow,
                            dibsCss.shrink,
                            dibsCss.basis0
                        )}
                    >
                        <LogoNav
                            user={user}
                            isUserTypeTrade={isUserTypeTrade}
                            isUserTypeVip={isUserTypeVip}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <div className={barClasses} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
            <div
                className={classnames(styles.barContent, dibsCss.flex, dibsCss.justifyBetween, {
                    [dibsCss.ptXsmall]: !isSticky,
                    [styles.isFullWidth]: isHeaderFullWidth,
                })}
                data-tn="main-nav-top"
            >
                <LogoNav
                    user={user}
                    isUserTypeTrade={isUserTypeTrade}
                    isUserTypeVip={isUserTypeVip}
                    isStickyLogo={isSticky}
                />
                <div className={classnames(dibsCss.wFull, dibsCss.mxLarger, dibsCss.mrSmall)}>
                    <SearchBar
                        viewer={viewer}
                        user={user}
                        isClient={isClient}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                    />
                </div>
                <div
                    className={classnames(
                        styles.rightContainer,
                        dibsCss.flex,
                        dibsCss.justifyEnd,
                        dibsCss.itemsCenter,
                        dibsCss.grow,
                        dibsCss.shrink0,
                        styles.basis261
                    )}
                >
                    <AccountNav
                        isClient={isClient}
                        hasUserId={hasUserId}
                        user={user}
                        hasTradeUserId={hasTradeUserId}
                        viewer={viewer}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        showDelayDuration={DROPDOWN_SHOW_DELAY_MS}
                        hideDelayDuration={DROPDOWN_HIDE_DELAY_MS}
                    />
                    {!isEmailOnly && userId && isClient && (
                        <UserNotifications
                            userId={userId}
                            hasTradeUserId={hasTradeUserId}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            showDelayDuration={DROPDOWN_SHOW_DELAY_MS}
                            hideDelayDuration={DROPDOWN_HIDE_DELAY_MS}
                        />
                    )}
                    <Favorites
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUserId={hasUserId}
                        isClient={isClient}
                        userId={userId}
                        showDelayDuration={DROPDOWN_SHOW_DELAY_MS}
                        hideDelayDuration={DROPDOWN_HIDE_DELAY_MS}
                    />
                    <div
                        className={classnames(
                            dibsCss.relative,
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.hFull
                        )}
                    >
                        <CartNav
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUserId={hasUserId}
                            isClient={isClient}
                            user={user}
                            userId={userId}
                            viewer={viewer}
                            showDelayDuration={DROPDOWN_SHOW_DELAY_MS}
                            hideDelayDuration={DROPDOWN_HIDE_DELAY_MS}
                        />
                        <CartAddedNotification />
                    </div>
                </div>
            </div>
        </div>
    );
};

export { TopBar };
